import {
  toWSDateString,
  useWSModalCustom,
  WSAlert,
  WSElement,
  WSFlexBox,
  WSForm,
  WSInfoBox,
  WSList,
  WSModal,
  WSPill,
  WSText
} from "@wingspanhq/fe-component-library";
import { PayeeRequirement } from "@wingspanhq/wingspan-api-sdk";
import React from "react";

import { ErrorAlert } from "@components/ErrorAlert";
import { DataItem } from "../../../../shared/components/DataItem";
import { queriesRequirements } from "../../../../v3/query/queriesRequirements";
import { useXWingspanPayerId } from "../../../Payees/routes/RoutePayeeDetails/useXWingspanPayerId";
import { RequirementStatusViewProps } from "../../types";

interface RenewRequirementProps {
  onClose: () => void;
  onSuccess: (requirement: PayeeRequirement) => void;
  requirement: PayeeRequirement;
  currentStatus: RequirementStatusViewProps;
}

const RenewRequirementModal: React.FC<RenewRequirementProps> = ({
  onClose,
  onSuccess,
  requirement,
  currentStatus
}) => {
  const [renewRequirement, renewRequirementMeta] =
    queriesRequirements.renewPayeeRequirement.useMutation();

  const xWingspanPayerId = useXWingspanPayerId();

  const onSubmit = () => {
    renewRequirement(
      {
        requirementId: requirement.id!,
        xWingspanAccount: xWingspanPayerId
      },
      {
        onSuccess: response => {
          onSuccess(response);
          onClose();
        }
      }
    );
  };

  return (
    <WSForm defaultValues={{}} onSubmit={onSubmit}>
      <WSModal
        actions={{
          alignment: "fill",
          buttons: [
            {
              kind: "Secondary",
              label: "Cancel",
              onClick: onClose,
              type: "button"
            },
            {
              kind: "Primary",
              label: "Start renewal",
              type: "submit",
              loading: renewRequirementMeta.isLoading
            }
          ]
        }}
        title="Start renewal"
        onClose={onClose}
      >
        <WSList gap="XL">
          <WSFlexBox gap="M" wrap="nowrap">
            <WSElement>
              <WSText.ParagraphSm color="gray600" mb="S">
                Current status:
              </WSText.ParagraphSm>
              <WSPill {...currentStatus.pill}></WSPill>
              <WSText.ParagraphXs color="gray400" ml="S" mt="XS">
                {currentStatus.secondaryText}
              </WSText.ParagraphXs>
            </WSElement>
          </WSFlexBox>

          <WSInfoBox>
            <DataItem
              label="Current expiration date"
              value={toWSDateString(requirement.expiresAt, "input")}
            />
            <WSText.ParagraphSm color="gray700" mt="L">
              We'll automatically remind you 30 days before the expiration date.
            </WSText.ParagraphSm>
          </WSInfoBox>

          <WSAlert
            icon="info-circle"
            theme="info"
            title="About starting renewals"
          >
            By proceeding, a renewal process will start that allows you to
            update your documents anytime before automatic reminders begin.
            Existing valid documents will remain current until they expire.
          </WSAlert>

          <ErrorAlert
            context="Other"
            error={renewRequirementMeta.isError}
            mt="XL"
          />
        </WSList>
      </WSModal>
    </WSForm>
  );
};

export function useRenewRequirementAsPayeeModal() {
  return useWSModalCustom(RenewRequirementModal);
}
